import { useMemo, useState } from "react";
import type { Currency, JournalEntry, JournalLine, Money, TaxCode } from "@core/index";
import { checkBalance, parseAmount, sumDebits, validateEntry, zero } from "@core/index";
import { useAppStore, selectCurrentRole } from "@state/store";
import { can } from "@data/index";
import { useLedger } from "@ui/useLedger";
import { MoneyText } from "@ui/components";

const CURRENCIES: Currency[] = ["AED", "USD", "EUR", "GBP"];
const TAX_CODES: { value: TaxCode | ""; label: string }[] = [
  { value: "", label: "None" },
  { value: "SR", label: "SR 5%" },
  { value: "ZR", label: "ZR 0%" },
  { value: "EX", label: "EX" },
  { value: "OS", label: "OS" },
];

interface FormLine {
  accountCode: string;
  taxCode: TaxCode | "";
  debit: string;
  credit: string;
}

function safeParse(input: string, base: Currency): { money: Money; invalid: boolean } {
  try {
    return { money: parseAmount(input || "0", base), invalid: false };
  } catch {
    return { money: zero(base), invalid: true };
  }
}

function nextRef(entries: readonly JournalEntry[]): string {
  const nums = entries
    .map((e) => /JE-(\d+)/.exec(e.ref)?.[1])
    .filter((n): n is string => Boolean(n))
    .map((n) => parseInt(n, 10));
  const next = (nums.length ? Math.max(...nums) : 1000) + 1;
  return `JE-${next}`;
}

function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `je-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
}

/** Base-currency minor units → an editable decimal string ("" for zero). */
function moneyToInput(m: Money): string {
  return m.amount === 0 ? "" : (m.amount / 100).toFixed(2);
}

export function Journal() {
  const { entities, accounts, entries, base, entityId } = useLedger();
  const addEntry = useAppStore((s) => s.addEntry);
  const updateEntry = useAppStore((s) => s.updateEntry);
  const canPostEntries = can(useAppStore(selectCurrentRole), "entry.post");

  const accountOptions = accounts;
  const defaultAccount = accountOptions[0]?.code ?? "";
  const emptyLine = (): FormLine => ({ accountCode: defaultAccount, taxCode: "", debit: "", credit: "" });

  const [date, setDate] = useState("2026-07-21");
  const [currency, setCurrency] = useState<Currency>("AED");
  const [description, setDescription] = useState("");
  const [formEntity, setFormEntity] = useState(entityId ?? entities[0]?.id ?? "");
  const [lines, setLines] = useState<FormLine[]>([emptyLine(), emptyLine()]);
  const [posted, setPosted] = useState<string | null>(null);
  // When set, the form is amending this existing entry rather than posting new.
  const [editing, setEditing] = useState<{ id: string; ref: string } | null>(null);

  // Keep the form entity in sync when there was none chosen yet.
  if (formEntity === "" && entities[0]) setFormEntity(entities[0].id);

  const parsedLines: JournalLine[] = useMemo(
    () =>
      lines.map((l) => ({
        accountCode: l.accountCode,
        debit: safeParse(l.debit, base).money,
        credit: safeParse(l.credit, base).money,
        ...(l.taxCode ? { taxCode: l.taxCode } : {}),
      })),
    [lines, base],
  );

  const check = checkBalance(parsedLines, base);
  const candidate: JournalEntry = {
    id: "preview",
    ref: editing ? editing.ref : nextRef(entries),
    date,
    description,
    entityId: formEntity,
    txnCurrency: currency,
    status: "posted",
    lines: parsedLines,
  };
  const validation = validateEntry(candidate);
  const anyInvalid = lines.some(
    (l) => safeParse(l.debit, base).invalid || safeParse(l.credit, base).invalid,
  );
  const canPost = validation.ok && !anyInvalid && formEntity !== "" && canPostEntries;

  function updateLine(i: number, patch: Partial<FormLine>) {
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines((prev) => [...prev, emptyLine()]);
  }
  function removeLine(i: number) {
    setLines((prev) => (prev.length <= 2 ? prev : prev.filter((_, idx) => idx !== i)));
  }

  function resetForm() {
    setDescription("");
    setLines([emptyLine(), emptyLine()]);
    setEditing(null);
  }

  async function post() {
    if (!canPost) return;
    if (editing) {
      const entry: JournalEntry = { ...candidate, id: editing.id };
      await updateEntry(entry);
      setPosted(`Updated ${entry.ref}`);
    } else {
      const entry: JournalEntry = { ...candidate, id: newId() };
      await addEntry(entry);
      setPosted(`Posted ${entry.ref}`);
    }
    resetForm();
    window.setTimeout(() => setPosted(null), 4000);
  }

  /** Load an existing entry into the form to amend it. */
  function editEntry(entry: JournalEntry) {
    setEditing({ id: entry.id, ref: entry.ref });
    setDate(entry.date);
    setCurrency(entry.txnCurrency);
    setDescription(entry.description);
    setFormEntity(entry.entityId);
    setLines(
      entry.lines.map((l) => ({
        accountCode: l.accountCode,
        taxCode: l.taxCode ?? "",
        debit: moneyToInput(l.debit),
        credit: moneyToInput(l.credit),
      })),
    );
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const postedList = useMemo(
    () =>
      [...entries]
        .filter((e) => e.status === "posted")
        .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
        .slice(0, 8),
    [entries],
  );

  const entityName = (id: string) => entities.find((e) => e.id === id)?.name ?? id;

  return (
    <section>
      <div className="page-head">
        <div>
          <h1>Journal</h1>
          <div className="sub">
            Double-entry, currency-aware. An entry can’t post until debits equal credits.
          </div>
        </div>
        <div className="actions">
          {!canPostEntries ? (
            <span className="role-note" role="note">
              Read-only — posting needs an Admin or Owner role.
            </span>
          ) : null}
          {editing ? (
            <button type="button" className="btn secondary" onClick={resetForm}>
              Cancel
            </button>
          ) : null}
          <button type="button" className="btn primary" onClick={post} disabled={!canPost}>
            {editing ? "Save changes" : "Post entry"}
          </button>
        </div>
      </div>

      {posted ? (
        <div className="card" style={{ marginBottom: "var(--s4)" }}>
          <div className="card-body">
            <span className="badge good">
              <span className="dot" /> {posted}
            </span>{" "}
            <span className="muted">— the ledger re-tied and statements updated.</span>
          </div>
        </div>
      ) : null}

      <div className="grid two">
        <div className="card">
          <div className="card-head">
            <h2>{editing ? `Edit entry ${editing.ref}` : "New entry"}</h2>
          </div>
          <div className="card-body">
            <div className="row" style={{ gap: "var(--s4)", alignItems: "flex-start" }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="jeDate">Date</label>
                <input
                  id="jeDate"
                  className="input"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="jeEntity">Entity</label>
                <select
                  id="jeEntity"
                  className="input"
                  value={formEntity}
                  onChange={(e) => setFormEntity(e.target.value)}
                >
                  {entities.map((en) => (
                    <option key={en.id} value={en.id}>
                      {en.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="jeCur">Currency</label>
                <select
                  id="jeCur"
                  className="input"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value as Currency)}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field mt4">
              <label htmlFor="jeDesc">Description</label>
              <input
                id="jeDesc"
                className="input"
                placeholder="e.g. Client invoice — Falcon Logistics"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="table-wrap mt4">
              <table className="data">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Tax code</th>
                    <th className="num">Debit</th>
                    <th className="num">Credit</th>
                    <th aria-label="Remove line" />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => {
                    const drInvalid = safeParse(l.debit, base).invalid;
                    const crInvalid = safeParse(l.credit, base).invalid;
                    return (
                      <tr key={i}>
                        <td>
                          <select
                            className="input"
                            aria-label={`Account for line ${i + 1}`}
                            value={l.accountCode}
                            onChange={(e) => updateLine(i, { accountCode: e.target.value })}
                          >
                            {accountOptions.map((a) => (
                              <option key={a.code} value={a.code}>
                                {a.code} · {a.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select
                            className="input"
                            aria-label={`Tax code for line ${i + 1}`}
                            value={l.taxCode}
                            onChange={(e) => updateLine(i, { taxCode: e.target.value as TaxCode | "" })}
                          >
                            {TAX_CODES.map((t) => (
                              <option key={t.label} value={t.value}>
                                {t.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            className={`input num${drInvalid ? " invalid" : ""}`}
                            inputMode="decimal"
                            aria-label={`Debit for line ${i + 1}`}
                            placeholder="0.00"
                            value={l.debit}
                            onChange={(e) => updateLine(i, { debit: e.target.value, credit: "" })}
                          />
                        </td>
                        <td>
                          <input
                            className={`input num${crInvalid ? " invalid" : ""}`}
                            inputMode="decimal"
                            aria-label={`Credit for line ${i + 1}`}
                            placeholder="0.00"
                            value={l.credit}
                            onChange={(e) => updateLine(i, { credit: e.target.value, debit: "" })}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="icon-btn"
                            aria-label={`Remove line ${i + 1}`}
                            onClick={() => removeLine(i)}
                            disabled={lines.length <= 2}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt3">
              <button type="button" className="btn secondary sm" onClick={addLine}>
                ＋ Add line
              </button>
            </div>

            <div className={`balance-meter mt4 ${check.balanced ? "ok" : "off"}`} aria-live="polite">
              <span className="dot" style={{ width: 8, height: 8, borderRadius: "50%", background: "currentColor" }} />
              <span>
                {check.empty ? (
                  "Enter debits and credits"
                ) : check.balanced ? (
                  "Balanced — ready to post"
                ) : (
                  <>
                    Out of balance by{" "}
                    <MoneyText
                      value={{ amount: Math.abs(check.delta.amount), currency: base }}
                      parenNegative={false}
                    />
                  </>
                )}
              </span>
              <span className="top-spacer" />
              <span className="mono tiny">
                DR <MoneyText value={check.debitTotal} /> · CR <MoneyText value={check.creditTotal} />
              </span>
            </div>

            {!validation.ok && !check.empty ? (
              <ul className="mt3 tiny muted" style={{ margin: "var(--s3) 0 0", paddingInlineStart: "var(--s5)" }}>
                {validation.errors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Posted entries</h2>
            <span className="top-spacer" />
            <span className="badge neutral">
              {entries.filter((e) => e.status === "posted").length} total
            </span>
          </div>
          <div className="card-body tight">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Ref</th>
                    <th>Description</th>
                    <th>Entity</th>
                    <th className="num">Amount</th>
                    {canPostEntries ? <th aria-label="Edit" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {postedList.map((e) => (
                    <tr key={e.id}>
                      <td>{e.date.slice(5)}</td>
                      <td className="acctcode">{e.ref}</td>
                      <td>{e.description}</td>
                      <td>{entityName(e.entityId)}</td>
                      <td className="num">
                        <MoneyText value={sumDebits(e.lines, base)} />
                      </td>
                      {canPostEntries ? (
                        <td>
                          <button
                            type="button"
                            className="btn secondary sm"
                            aria-label={`Edit ${e.ref}`}
                            onClick={() => editEntry(e)}
                          >
                            Edit
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
